import { useCallback, useEffect, useRef } from 'react';

import { useStableCallback } from '../../utils/hooks';

interface MessageWaiter {
    resolve: (data: MessageEvent['data']) => void;
    testMessage: (data: MessageEvent['data']) => boolean;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function useWindowMessage(handleMessage?: (data: any) => void) {
    const messageWaiters = useRef<MessageWaiter[]>([]);
    const handler = useStableCallback(handleMessage);

    const waitMessage = useCallback(
        async <T>(testMessage: MessageWaiter['testMessage'], timeout?: number) =>
            new Promise<T>((resolve, reject) => {
                timeout = typeof timeout === 'number' ? timeout : 15000;
                const waiter = { testMessage, resolve };
                messageWaiters.current.push(waiter);
                setTimeout(() => {
                    reject();
                    messageWaiters.current = messageWaiters.current.filter((w) => w !== waiter);
                }, timeout);
            }),
        []
    );

    useEffect(() => {
        const onMessage = (event: MessageEvent) => {
            try {
                if (typeof event.data !== 'string') {
                    return;
                }
                const data = JSON.parse(event.data);
                handler?.(data);
                messageWaiters.current = messageWaiters.current.filter(({ testMessage, resolve }) => {
                    if (testMessage(data)) {
                        resolve(data);
                        return false;
                    }
                    return true;
                });
            } catch {
                //
            }
        };

        window.addEventListener('message', onMessage);
        return () => window.removeEventListener('message', onMessage);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return { waitMessage } as const;
}
